import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";

import { logout } from "@/api";
import axios from "@/api/axios";
import useAccountsStore from "@/stores/useAccountsStore";
import useLocalStorage from "@/hooks/useLocalStorage";
import { useTheme } from "@/ThemeProvider";
import { useSubscription, useCustomerPortal } from "@/hooks/useSubscription";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useAccounts } from "@/hooks/hooks";

/**
 * Account-level actions shared by the desktop icon rail (OuterSidebar) and the
 * mobile account menu. Callers own their dialog state (logout confirm, upgrade)
 * and the paywall decision: check `isAtAccountLimit` before `connectAccount()`.
 *
 * The `taskfusion:reauth` listener is intentionally NOT registered here — it
 * must live in exactly one always-mounted component (OuterSidebar) so a reauth
 * event triggers a single consent flow.
 */
export const useAccountActions = () => {
  const reset = useAccountsStore((state) => state.reset);
  const addAccount = useAccountsStore((state) => state.addAccount);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [, setPrimaryAccountId] = useLocalStorage<string | null>("primaryAccountId", null);
  const { theme, setTheme } = useTheme();
  const { data: subscription } = useSubscription();
  const { data: accounts } = useAccounts();
  const customerPortal = useCustomerPortal();
  const analytics = useAnalytics();

  const isPaid = subscription?.plan === "pro" || subscription?.plan === "duo";
  const planBadge = subscription?.plan === "pro" ? "PRO" : subscription?.plan === "duo" ? "DUO" : "FREE";
  const trialDaysLeft =
    subscription?.trialActive && subscription.trialEndsAt
      ? Math.max(0, Math.ceil((new Date(subscription.trialEndsAt).getTime() - Date.now()) / 86400000))
      : null;

  const handleLogout = async () => {
    analytics.track("logout");
    analytics.reset();
    await logout();
    setPrimaryAccountId(null);
    reset();
    queryClient.removeQueries();
    navigate("/login");
  };

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const handleManageSubscription = async () => {
    try {
      const url = await customerPortal.mutateAsync();
      // window.open after an await is popup-blocked on iOS; navigate in-place
      // on mobile instead (matches the checkout flow in UpgradeDialog)
      if (window.matchMedia("(max-width: 767px)").matches) {
        toast.loading("Opening billing portal...");
        window.location.assign(url);
      } else {
        window.open(url, "_blank");
      }
    } catch {
      console.error("Failed to open customer portal");
    }
  };

  const connectAccount = useGoogleLogin({
    onSuccess: async (codeResponse: { code: string }) => {
      try {
        const res = await axios.post(
          "/addAccount",
          { code: codeResponse.code },
          { withCredentials: true }
        );
        const user = res.data;
        addAccount(user);

        const newCount = (accounts ? Object.keys(accounts).length : 1) + 1;
        analytics.track("account_added", { added_account_email: user.email });
        analytics.setPersonProps({ accounts_connected: newCount });
        analytics.updateSuperProperties({ accounts_connected: newCount });

        queryClient.invalidateQueries({ queryKey: ["accounts"] });
      } catch (error) {
        const err = error as { response?: { status?: number; data?: { error?: string } } };
        if (err?.response?.status === 409 && err?.response?.data?.error === "stale_scopes") {
          toast.info("We've updated the permissions Task Fusion uses. Please connect this account once more.");
        } else {
          console.log("error", error);
        }
      }
    },
    onError: (codeResponse) => console.log(codeResponse),
    flow: "auth-code",
    ux_mode: "popup",
    select_account: true,
    scope: "https://www.googleapis.com/auth/tasks",
  });

  // Gate adding accounts at the plan's limit — this is where the paywall earns its keep
  const accountLimit = subscription?.accountLimit; // null/undefined = unlimited
  const connectedCount = accounts ? Object.keys(accounts).length : 0;
  const isAtAccountLimit = accountLimit != null && connectedCount >= accountLimit;

  return {
    subscription,
    isPaid,
    planBadge,
    trialDaysLeft,
    theme,
    toggleTheme,
    handleLogout,
    handleManageSubscription,
    connectAccount,
    isAtAccountLimit,
  };
};
